/**
 * Intake forms: field model, conditional visibility, validation and missing-information flags.
 *
 * Submitting never fails just because optional-to-the-client details are missing: only the core
 * contact fields (name plus a way to reach the person) and malformed values block a submission.
 * Every other required-but-empty visible field becomes a "missing information" flag; the intake
 * goes to `needs_info` and LawBee emails the prospect a link to fill in exactly those fields.
 */

export const FIELD_TYPES = ['text', 'textarea', 'email', 'phone', 'date', 'number', 'select', 'multiselect', 'checkbox', 'file', 'parties'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export type ShowIf = { field: string; equals?: string | boolean; in?: string[]; notEmpty?: boolean };

export type IntakeField = {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  help?: string;
  options?: string[];
  showIf?: ShowIf;
  /** Parties fields feed the conflict check (other side, related people/companies). */
  partyRole?: string;
};

/** Core fields every form has (rendered first, mapped to intake columns). */
export const CORE_FIELDS: IntakeField[] = [
  { key: 'first_name', label: 'First name', type: 'text', required: true },
  { key: 'last_name', label: 'Last name', type: 'text', required: true },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'phone', label: 'Phone', type: 'phone' },
];
export const CORE_KEYS = new Set(CORE_FIELDS.map((f) => f.key));

export type Party = { name: string; role: string };
export type AnswerValue = string | number | boolean | string[] | Party[] | null;
export type Answers = Record<string, AnswerValue | undefined>;

export type FieldError = { field: string; message: string };
export type MissingFlag = { field: string; label: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-.\s\d]{7,20}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const KEY_RE = /^[a-z][a-z0-9_]{0,47}$/;

export function isEmpty(v: AnswerValue | undefined): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'boolean') return v === false;
  return false;
}

/** Whether a field is shown given the current answers (conditional fields). */
export function isVisible(field: IntakeField, answers: Answers, fields: IntakeField[] = []): boolean {
  const cond = field.showIf;
  if (!cond) return true;
  const parent = fields.find((f) => f.key === cond.field);
  // A field that depends on a hidden field is hidden too.
  if (parent && !isVisible(parent, answers, fields)) return false;
  const v = answers[cond.field];
  if (cond.notEmpty) return !isEmpty(v);
  if (cond.in) return typeof v === 'string' ? cond.in.includes(v) : Array.isArray(v) ? (v as unknown[]).some((x) => typeof x === 'string' && cond.in!.includes(x)) : false;
  if (cond.equals !== undefined) {
    if (typeof cond.equals === 'boolean') return (v === true || v === 'true' || v === 'yes') === cond.equals;
    return v === cond.equals || (Array.isArray(v) && (v as unknown[]).includes(cond.equals));
  }
  return true;
}

/** Validate a form definition (form builder). Returns problems, empty when valid. */
export function validateFormDefinition(fields: IntakeField[]): string[] {
  const problems: string[] = [];
  const keys = new Set<string>();
  for (const f of fields) {
    if (!KEY_RE.test(f.key)) problems.push(`Field key "${f.key}" must be lowercase letters, digits and underscores.`);
    if (CORE_KEYS.has(f.key)) problems.push(`"${f.key}" is a built-in contact field.`);
    if (keys.has(f.key)) problems.push(`Duplicate field key "${f.key}".`);
    keys.add(f.key);
    if (!(FIELD_TYPES as readonly string[]).includes(f.type)) problems.push(`Unknown field type "${f.type}".`);
    if (!f.label?.trim()) problems.push(`Field "${f.key}" needs a label.`);
    if ((f.type === 'select' || f.type === 'multiselect') && !(f.options && f.options.length > 0)) problems.push(`"${f.label}" needs at least one option.`);
  }
  for (const f of fields) {
    if (f.showIf && !keys.has(f.showIf.field) && !CORE_KEYS.has(f.showIf.field)) problems.push(`"${f.label}" depends on unknown field "${f.showIf.field}".`);
    if (f.showIf?.field === f.key) problems.push(`"${f.label}" cannot depend on itself.`);
  }
  return problems;
}

function formatError(field: IntakeField, v: AnswerValue | undefined): string | null {
  if (isEmpty(v)) return null;
  switch (field.type) {
    case 'email':
      return typeof v === 'string' && EMAIL_RE.test(v.trim()) ? null : 'Enter a valid email address.';
    case 'phone':
      return typeof v === 'string' && PHONE_RE.test(v.trim()) ? null : 'Enter a valid phone number.';
    case 'date':
      return typeof v === 'string' && DATE_RE.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) ? null : 'Enter a date as YYYY-MM-DD.';
    case 'number':
      return (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) ? null : 'Enter a number.';
    case 'select':
      return typeof v === 'string' && (field.options ?? []).includes(v) ? null : 'Choose one of the options.';
    case 'multiselect':
      return Array.isArray(v) && (v as unknown[]).every((x) => typeof x === 'string' && (field.options ?? []).includes(x)) ? null : 'Choose from the options.';
    case 'parties':
      return Array.isArray(v) && (v as unknown[]).every((p) => typeof p === 'object' && p !== null && typeof (p as Party).name === 'string') ? null : 'List each party with a name.';
    case 'text':
    case 'textarea':
      return typeof v === 'string' && v.length <= 10_000 ? null : 'Too long.';
    default:
      return null;
  }
}

export type IntakeValidation = {
  /** Problems that block submission (bad format, missing name / contact details). */
  errors: FieldError[];
  /** Required visible fields left empty: flagged for follow-up, do not block. */
  missing: MissingFlag[];
  /** Answers restricted to visible, known fields, trimmed. */
  clean: Answers;
  /** Everyone named for the conflict check (from parties fields). */
  parties: Party[];
};

/**
 * Validate answers against a form. `fileKeys` lists file fields that received an upload (files are
 * stored separately, so they count as answered).
 */
export function validateIntake(fields: IntakeField[], answers: Answers, fileKeys: string[] = []): IntakeValidation {
  const all = [...CORE_FIELDS, ...fields];
  const errors: FieldError[] = [];
  const missing: MissingFlag[] = [];
  const clean: Answers = {};
  const parties: Party[] = [];
  for (const field of all) {
    if (!isVisible(field, answers, all)) continue;
    let v = answers[field.key];
    if (typeof v === 'string') v = v.trim();
    if (field.type === 'file' && fileKeys.includes(field.key)) v = 'uploaded';
    const fmt = formatError(field, v);
    if (fmt) {
      errors.push({ field: field.key, message: fmt });
      continue;
    }
    if (field.type === 'parties' && Array.isArray(v)) {
      const list = (v as Party[])
        .map((p) => ({ name: String(p.name ?? '').trim(), role: String(p.role ?? field.partyRole ?? 'related_party').trim() || 'related_party' }))
        .filter((p) => p.name);
      v = list;
      parties.push(...list);
    }
    if (isEmpty(v)) {
      if (field.required && field.type !== 'checkbox') missing.push({ field: field.key, label: field.label });
      continue;
    }
    if (field.type !== 'file') clean[field.key] = v;
  }
  // Core rules that do block: a name and at least one way to reach the person.
  for (const key of ['first_name', 'last_name'] as const) {
    if (isEmpty(clean[key]) && !errors.some((e) => e.field === key)) errors.push({ field: key, message: 'Required.' });
  }
  if (isEmpty(clean.email) && isEmpty(clean.phone) && !errors.some((e) => e.field === 'email' || e.field === 'phone')) {
    errors.push({ field: 'email', message: 'Give an email or a phone number so the firm can reach you.' });
  }
  const blocking = new Set(errors.map((e) => e.field));
  // Core fields are covered by the blocking rules above, never by follow-up flags.
  return { errors, missing: missing.filter((m) => !blocking.has(m.field) && !CORE_KEYS.has(m.field)), clean, parties };
}

/**
 * Merge follow-up answers into an existing intake: only fields that were flagged missing (or are
 * newly visible because of them) may be filled; returns the recomputed validation.
 */
export function mergeFollowUp(fields: IntakeField[], existing: Answers, update: Answers, fileKeys: string[] = []): IntakeValidation {
  const merged: Answers = { ...existing };
  for (const [k, v] of Object.entries(update)) {
    if (CORE_KEYS.has(k) && k !== 'phone' && k !== 'email') continue;
    if (isEmpty(existing[k])) merged[k] = v;
  }
  return validateIntake(fields, merged, fileKeys);
}

export type IntakeStatus = 'new' | 'needs_info' | 'in_review' | 'converted' | 'declined';

/** Status after a submission or follow-up. */
export function statusAfterSubmit(missing: MissingFlag[]): IntakeStatus {
  return missing.length > 0 ? 'needs_info' : 'new';
}

// ---------------------------------------------------------------------------
// Presets per practice area (form builder starting points)
// ---------------------------------------------------------------------------

export const PRACTICE_AREAS = ['family', 'personal_injury', 'real_estate', 'wills_estates', 'immigration', 'corporate'] as const;
export type PracticeArea = (typeof PRACTICE_AREAS)[number];

export const PRACTICE_AREA_LABELS: Record<PracticeArea, string> = {
  family: 'Family law',
  personal_injury: 'Personal injury',
  real_estate: 'Real estate',
  wills_estates: 'Wills and estates',
  immigration: 'Immigration',
  corporate: 'Corporate and business',
};

const otherSide = (label = 'Other parties involved (for our conflict check)', role = 'opposing_party'): IntakeField => ({
  key: 'other_parties',
  label,
  type: 'parties',
  required: true,
  partyRole: role,
  help: 'Names only. We use them to check for conflicts of interest before we can act for you.',
});

export const INTAKE_PRESETS: Record<PracticeArea, { name: string; intro: string; fields: IntakeField[] }> = {
  family: {
    name: 'Family law intake',
    intro: 'Tell us a little about your situation so a lawyer can prepare for your consultation.',
    fields: [
      { key: 'matter', label: 'What do you need help with?', type: 'select', required: true, options: ['Separation or divorce', 'Parenting / custody', 'Child or spousal support', 'Property division', 'Marriage or cohabitation agreement', 'Other'] },
      { key: 'relationship', label: 'Relationship', type: 'select', required: true, options: ['Married', 'Common-law', 'Dating / never lived together'] },
      { key: 'separation_date', label: 'Date of separation', type: 'date', required: true, showIf: { field: 'matter', in: ['Separation or divorce', 'Property division', 'Child or spousal support'] } },
      { key: 'has_children', label: 'Do you have children together?', type: 'select', required: true, options: ['Yes', 'No'] },
      { key: 'children_ages', label: 'Ages of the children', type: 'text', required: true, showIf: { field: 'has_children', equals: 'Yes' } },
      { key: 'court_case', label: 'Is there already a court case?', type: 'select', required: true, options: ['Yes', 'No', 'Not sure'] },
      { key: 'court_file', label: 'Court file number', type: 'text', showIf: { field: 'court_case', equals: 'Yes' } },
      { key: 'safety_concern', label: 'Do you have any safety concerns?', type: 'select', options: ['No', 'Yes'], help: 'If you are in danger, call 911.' },
      otherSide('Spouse / partner and anyone else involved (for our conflict check)'),
      { key: 'documents', label: 'Court papers or agreements (optional)', type: 'file' },
      { key: 'summary', label: 'Briefly describe your situation', type: 'textarea', required: true },
    ],
  },
  personal_injury: {
    name: 'Personal injury intake',
    intro: 'A few details about what happened. A lawyer will review them before your consultation.',
    fields: [
      { key: 'incident_type', label: 'Type of incident', type: 'select', required: true, options: ['Motor vehicle collision', 'Slip and fall', 'Medical', 'Workplace', 'Other'] },
      { key: 'incident_date', label: 'Date of the incident', type: 'date', required: true },
      { key: 'incident_location', label: 'Where did it happen?', type: 'text', required: true },
      { key: 'injuries', label: 'Injuries', type: 'textarea', required: true },
      { key: 'treatment', label: 'Have you seen a doctor?', type: 'select', required: true, options: ['Yes', 'No'] },
      { key: 'insurer', label: 'Your insurance company', type: 'text', showIf: { field: 'incident_type', equals: 'Motor vehicle collision' } },
      { key: 'police_report', label: 'Was a police report made?', type: 'select', options: ['Yes', 'No', 'Not sure'], showIf: { field: 'incident_type', equals: 'Motor vehicle collision' } },
      otherSide('Other drivers, property owners or companies involved (for our conflict check)'),
      { key: 'documents', label: 'Photos, reports or medical notes (optional)', type: 'file' },
      { key: 'summary', label: 'What happened?', type: 'textarea', required: true },
    ],
  },
  real_estate: {
    name: 'Real estate intake',
    intro: 'Tell us about the property and the transaction.',
    fields: [
      { key: 'transaction', label: 'Transaction', type: 'select', required: true, options: ['Purchase', 'Sale', 'Refinance', 'Title transfer'] },
      { key: 'property_address', label: 'Property address', type: 'text', required: true },
      { key: 'closing_date', label: 'Closing date', type: 'date', required: true, showIf: { field: 'transaction', in: ['Purchase', 'Sale'] } },
      { key: 'price', label: 'Purchase / sale price (CAD)', type: 'number', showIf: { field: 'transaction', in: ['Purchase', 'Sale'] } },
      { key: 'lender', label: 'Mortgage lender', type: 'text', showIf: { field: 'transaction', in: ['Purchase', 'Refinance'] } },
      otherSide('Buyers / sellers and their agents (for our conflict check)', 'related_party'),
      { key: 'documents', label: 'Agreement of purchase and sale (optional)', type: 'file' },
      { key: 'summary', label: 'Anything else we should know?', type: 'textarea' },
    ],
  },
  wills_estates: {
    name: 'Wills and estates intake',
    intro: 'Planning a will or handling an estate. Share what you can; we will follow up on the rest.',
    fields: [
      { key: 'service', label: 'Service', type: 'select', required: true, options: ['Will and powers of attorney', 'Estate administration (probate)', 'Estate dispute'] },
      { key: 'marital_status', label: 'Marital status', type: 'select', options: ['Single', 'Married', 'Common-law', 'Separated', 'Widowed'], showIf: { field: 'service', equals: 'Will and powers of attorney' } },
      { key: 'deceased_name', label: 'Name of the person who passed away', type: 'text', required: true, showIf: { field: 'service', in: ['Estate administration (probate)', 'Estate dispute'] } },
      { key: 'date_of_death', label: 'Date of death', type: 'date', required: true, showIf: { field: 'service', in: ['Estate administration (probate)', 'Estate dispute'] } },
      { key: 'has_will', label: 'Is there a will?', type: 'select', options: ['Yes', 'No', 'Not sure'], showIf: { field: 'service', in: ['Estate administration (probate)', 'Estate dispute'] } },
      otherSide('Beneficiaries, executors and other family members (for our conflict check)', 'related_party'),
      { key: 'summary', label: 'Briefly describe what you need', type: 'textarea', required: true },
    ],
  },
  immigration: {
    name: 'Immigration intake',
    intro: 'Tell us about your immigration goals.',
    fields: [
      { key: 'goal', label: 'What are you applying for?', type: 'select', required: true, options: ['Work permit', 'Study permit', 'Permanent residence', 'Family sponsorship', 'Citizenship', 'Refugee claim', 'Other'] },
      { key: 'citizenship', label: 'Country of citizenship', type: 'text', required: true },
      { key: 'in_canada', label: 'Are you currently in Canada?', type: 'select', required: true, options: ['Yes', 'No'] },
      { key: 'status_expiry', label: 'When does your current status expire?', type: 'date', required: true, showIf: { field: 'in_canada', equals: 'Yes' } },
      { key: 'sponsor_name', label: 'Sponsor name', type: 'text', required: true, showIf: { field: 'goal', equals: 'Family sponsorship' } },
      { key: 'prior_refusal', label: 'Any previous refusals?', type: 'select', options: ['No', 'Yes'] },
      otherSide('Employers, sponsors or family members involved (for our conflict check)', 'related_party'),
      { key: 'documents', label: 'Passport or permits (optional)', type: 'file' },
      { key: 'summary', label: 'Anything else we should know?', type: 'textarea' },
    ],
  },
  corporate: {
    name: 'Business law intake',
    intro: 'Tell us about your business and what you need.',
    fields: [
      { key: 'service', label: 'Service', type: 'select', required: true, options: ['Incorporation', 'Shareholder agreement', 'Contract review', 'Purchase or sale of a business', 'Other'] },
      { key: 'company_name', label: 'Company name (if any)', type: 'text' },
      { key: 'jurisdiction', label: 'Province of incorporation', type: 'select', options: ['Federal', 'ON', 'BC', 'AB', 'QC', 'Other'] },
      { key: 'counterparty', label: 'Who is on the other side of the deal?', type: 'text', showIf: { field: 'service', in: ['Contract review', 'Purchase or sale of a business'] } },
      otherSide('Other companies, shareholders or individuals involved (for our conflict check)', 'related_party'),
      { key: 'deadline', label: 'Any deadline?', type: 'date' },
      { key: 'summary', label: 'Briefly describe what you need', type: 'textarea', required: true },
    ],
  },
};
